import { lazy, Suspense } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/hooks/api/useAuth';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { PublicRoute } from '@/components/auth/PublicRoute';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { AppLayout } from '@/components/layout/AppLayout';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2 } from 'lucide-react';

// ==================== CODE SPLITTING ====================
// Lazy load pages to reduce initial bundle size
// Expected reduction: ~40% (1,951kB -> ~1,170kB initial bundle)

// Public pages (loaded on-demand)
const Login = lazy(() => import('./pages/Login'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Protected pages (loaded on-demand)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Index = lazy(() => import('./pages/Index'));
const OrderPackageDetail = lazy(() => import('./pages/OrderPackageDetail'));
const ImportOrder = lazy(() => import('./pages/ImportOrder'));
const ImportBilling = lazy(() => import('./pages/ImportBilling'));
const OrdersTracking = lazy(() => import('./pages/OrdersTracking'));
const Admin = lazy(() => import('./pages/Admin'));
const MyTimeReport = lazy(() => import('./pages/MyTimeReport'));
const Reports = lazy(() => import('./pages/Reports'));
const PlannedBillingReport = lazy(() => import('./pages/PlannedBillingReport'));
const PlannedPackagesReport = lazy(() => import('./pages/PlannedPackagesReport'));
const PlannedVsBilledReport = lazy(() => import('./pages/PlannedVsBilledReport'));
const PendingItemsReport = lazy(() => import('./pages/PendingItemsReport'));
const SalesByMonthReport = lazy(() => import('./pages/SalesByMonthReport'));
const SalesByClientReport = lazy(() => import('./pages/SalesByClientReport'));
const SalesByLocationReport = lazy(() => import('./pages/SalesByLocationReport'));
const Profile = lazy(() => import('./pages/Profile'));
const WorksCalendar = lazy(() => import('./pages/WorksCalendar'));
const GanttChart = lazy(() => import('./pages/GanttChart'));

// Loading fallback component — exibido enquanto o bundle JS da página (code-split) é baixado.
// Reaproveita o AppLayout real (sidebar + header) para evitar o flash de tela branca durante a navegação
// entre páginas autenticadas. Fora de uma sessão autenticada (ex: tela de login), usa um spinner simples
// para não piscar a sidebar nem disparar chamadas de API que exigem autenticação.
const PageLoader = () => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <AppLayout>
      <div className="container mx-auto p-6 space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-9 w-56" />
          <Skeleton className="h-5 w-72" />
        </div>
        <Skeleton className="h-40 w-full rounded-lg" />
        <Skeleton className="h-96 w-full rounded-lg" />
      </div>
    </AppLayout>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Desabilitar refetch ao focar janela globalmente (otimização de performance)
      // Habilitar apenas em queries específicas onde necessário
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      staleTime: 5 * 60 * 1000, // 5 minutos
      cacheTime: 10 * 60 * 1000, // 10 minutos
      retry: (failureCount, error: any) => {
        // Não fazer retry em erros 4xx (erro do cliente)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry até 3 vezes em erros 5xx (erro do servidor)
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => {
        // Exponential backoff: 1s, 2s, 4s, máximo 30s
        return Math.min(1000 * 2 ** attemptIndex, 30000);
      },
      onError: (error: any) => {
        // Log centralizado de erros de queries
        console.error('Query error:', error);
      },
    },
    mutations: {
      // Mutations não devem ter retry automático
      retry: false,
      onError: (error: any) => {
        console.error('Mutation error:', error);
      },
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />

                {/* Protected Routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/kanban"
                  element={
                    <ProtectedRoute>
                      <Index />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orders-tracking"
                  element={
                    <ProtectedRoute>
                      <OrdersTracking />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/order/:id"
                  element={
                    <ProtectedRoute>
                      <OrderPackageDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/import"
                  element={
                    <ProtectedRoute>
                      <ImportOrder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/import-billing"
                  element={
                    <ProtectedRoute>
                      <ImportBilling />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/*"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Admin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-hours"
                  element={
                    <ProtectedRoute>
                      <MyTimeReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute>
                      <Reports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/planned-billing"
                  element={
                    <ProtectedRoute>
                      <PlannedBillingReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/planned"
                  element={
                    <ProtectedRoute>
                      <PlannedPackagesReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/planned-vs-billed"
                  element={
                    <ProtectedRoute>
                      <PlannedVsBilledReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/pending-items"
                  element={
                    <ProtectedRoute>
                      <PendingItemsReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/sales-by-month"
                  element={
                    <ProtectedRoute>
                      <SalesByMonthReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/sales-by-client"
                  element={
                    <ProtectedRoute>
                      <SalesByClientReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/sales-by-location"
                  element={
                    <ProtectedRoute>
                      <SalesByLocationReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/gantt"
                  element={
                    <ProtectedRoute>
                      <GanttChart />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/works-calendar"
                  element={
                    <ProtectedRoute>
                      <WorksCalendar />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
